<div class="page-container">

  <!-- Report Regeneration -->
  <p-card class="card-section m-5">
    <ng-template pTemplate="header">
      <div class="p-3">
        <h2 class="m-0"><i class="pi pi-file-excel mr-2"></i>Report Generation</h2>
        <p class="text-color-secondary mt-1 mb-0">Generate or regenerate a monthly fuel report</p>
      </div>
    </ng-template>

    <div class="formgrid grid align-items-end">
      <div class="field col-12 md:col-3">
        <label class="font-semibold">Year</label>
        <p-dropdown
          [options]="yearOptions"
          [(ngModel)]="selectedYear"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full">
        </p-dropdown>
      </div>
      <div class="field col-12 md:col-3">
        <label class="font-semibold">Month</label>
        <p-dropdown
          [options]="monthOptions"
          [(ngModel)]="selectedMonth"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full">
        </p-dropdown>
      </div>
      <div class="field col-12 md:col-6">
        <label class="font-semibold block mb-2">Options</label>
        <div class="flex gap-4 flex-wrap">
          <div class="flex align-items-center gap-2">
            <p-checkbox [(ngModel)]="overrideDropbox" [binary]="true" inputId="overrideDropbox"></p-checkbox>
            <label for="overrideDropbox">Override Dropbox file</label>
            <i class="pi pi-info-circle option-info"
               pTooltip="If a report already exists in Dropbox for the selected month and year, it will be replaced with the newly generated file."
               tooltipPosition="top"></i>
          </div>
          <div class="flex align-items-center gap-2">
            <p-checkbox [(ngModel)]="emailReport" [binary]="true" inputId="emailReport"></p-checkbox>
            <label for="emailReport">Email report</label>
            <i class="pi pi-info-circle option-info"
               pTooltip="Automatically sends the generated report to all configured recipients once it has been created."
               tooltipPosition="top"></i>
          </div>
          <div class="flex align-items-center gap-2">
            <p-checkbox [(ngModel)]="downloadReport" [binary]="true" inputId="downloadReport"></p-checkbox>
            <label for="downloadReport">Download locally</label>
            <i class="pi pi-info-circle option-info"
               pTooltip="Downloads the generated report file directly to your device instead of, or in addition to, saving it to Dropbox."
               tooltipPosition="top"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Recipient fields — visible only when Email report is checked -->
    <div *ngIf="emailReport" class="email-recipients-panel mt-3">
      <div class="formgrid grid">
        <div class="field col-12 md:col-6">
          <label class="font-semibold block mb-2">
            <i class="pi pi-send mr-1"></i>To
          </label>
          <p-chips
            [(ngModel)]="emailTo"
            placeholder="Add email and press Enter"
            styleClass="w-full"
            [allowDuplicate]="false"
            addOnBlur="true">
          </p-chips>
          <small class="text-color-secondary">Press Enter or Tab after each address</small>
        </div>
        <div class="field col-12 md:col-6">
          <label class="font-semibold block mb-2">
            <i class="pi pi-copy mr-1"></i>CC
          </label>
          <p-chips
            [(ngModel)]="emailCc"
            placeholder="Add email and press Enter"
            styleClass="w-full"
            [allowDuplicate]="false"
            addOnBlur="true">
          </p-chips>
          <small class="text-color-secondary">Leave empty to send no CC</small>
        </div>
      </div>
    </div>

    <div class="flex justify-content-end mt-2">
      <button type="button"
        class="p-button"
        [disabled]="isGenerating || (emailReport && emailTo.length === 0)"
        (click)="onGenerateReport()">
        <i [class]="isGenerating ? 'pi pi-spin pi-spinner mr-2' : 'pi pi-refresh mr-2'"></i>
        {{ isGenerating ? 'Generating...' : 'Generate Report' }}
      </button>
    </div>
  </p-card>

  <!-- Analytics Filters -->
  <p-card class="card-section">
    <ng-template pTemplate="header">
      <div class="p-3">
        <h2 class="m-0"><i class="pi pi-chart-bar mr-2"></i>Fuel Analytics</h2>
      </div>
    </ng-template>

    <div class="formgrid grid align-items-end mb-3">
      <div class="field col-12 md:col-3">
        <label class="font-semibold">Filter Year</label>
        <p-dropdown
          [options]="yearOptions"
          [(ngModel)]="filterYear"
          optionLabel="label"
          optionValue="value"
          [showClear]="true"
          placeholder="All years"
          styleClass="w-full"
          (onChange)="onFilterChange()">
        </p-dropdown>
      </div>
      <div class="field col-12 md:col-3">
        <label class="font-semibold">Filter Month</label>
        <p-dropdown
          [options]="monthOptions"
          [(ngModel)]="filterMonth"
          optionLabel="label"
          optionValue="value"
          [showClear]="true"
          placeholder="All months"
          styleClass="w-full"
          (onChange)="onFilterChange()">
        </p-dropdown>
      </div>
      <div class="field col-12 md:col-2">
        <button type="button"
          class="p-button p-button-secondary p-button-outlined"
          (click)="loadAnalytics()">
          <i class="pi pi-refresh mr-2"></i>Refresh
        </button>
      </div>
    </div>

    <p-progressBar *ngIf="isLoadingCharts" mode="indeterminate" style="height: 4px"></p-progressBar>

    <!-- Charts Grid -->
    <div class="grid" *ngIf="!isLoadingCharts">

      <!-- Monthly Gallons Trend -->
      <div class="col-12">
        <p-card>
          <h4 class="mt-0">Monthly Total Gallons Trend</h4>
          <p-chart *ngIf="hasData(monthlyGallonsData); else noData_monthly"
            type="line"
            [data]="monthlyGallonsData"
            [options]="monthlyGallonsOptions"
            height="300px">
          </p-chart>
          <ng-template #noData_monthly><div class="chart-empty-state">No Data Available</div></ng-template>
        </p-card>
      </div>

      <!-- Per Truck -->
      <div class="col-12 lg:col-6">
        <p-card>
          <h4 class="mt-0">Fuel Usage per Truck (Top 15)</h4>
          <p-chart *ngIf="hasData(perTruckData); else noData_truck"
            type="bar"
            [data]="perTruckData"
            [options]="perTruckOptions"
            height="300px">
          </p-chart>
          <ng-template #noData_truck><div class="chart-empty-state">No Data Available</div></ng-template>
        </p-card>
      </div>

      <!-- Vendor Breakdown -->
      <div class="col-12 lg:col-6">
        <p-card>
          <h4 class="mt-0">Vendor Breakdown</h4>
          <p-chart *ngIf="hasData(vendorBreakdownData); else noData_vendor"
            type="pie"
            [data]="vendorBreakdownData"
            [options]="vendorBreakdownOptions"
            height="300px">
          </p-chart>
          <ng-template #noData_vendor><div class="chart-empty-state">No Data Available</div></ng-template>
        </p-card>
      </div>

      <!-- State Breakdown -->
      <div class="col-12 lg:col-6">
        <p-card>
          <h4 class="mt-0">Geographic Breakdown by State</h4>
          <p-chart *ngIf="hasData(stateBreakdownData); else noData_state"
            type="bar"
            [data]="stateBreakdownData"
            [options]="stateBreakdownOptions"
            height="300px">
          </p-chart>
          <ng-template #noData_state><div class="chart-empty-state">No Data Available</div></ng-template>
        </p-card>
      </div>

      <!-- Yard vs Road -->
      <div class="col-12 lg:col-6">
        <p-card>
          <h4 class="mt-0">Yard Fills vs. External Fills</h4>
          <p-chart *ngIf="hasData(yardVsRoadData); else noData_yard"
            type="bar"
            [data]="yardVsRoadData"
            [options]="yardVsRoadOptions"
            height="300px">
          </p-chart>
          <ng-template #noData_yard><div class="chart-empty-state">No Data Available</div></ng-template>
        </p-card>
      </div>

    </div>
  </p-card>
</div>
